import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Minus, PlugZap } from 'lucide-react';
import { BRAND } from '@/lib/brand';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatBucket, formatUnit } from '@/features/intelligence/dashboard/series';
import type { MetricDirection, TimeGrain, Unit } from '@/features/intelligence/dashboard/types';

export function WidgetSkeleton({ variant }: { variant: 'metric' | 'chart' | 'table' }) {
  if (variant === 'metric') {
    return (
      <div className="flex h-full flex-col justify-between gap-2">
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-3 w-36" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }
  if (variant === 'table') {
    return (
      <div className="flex h-full flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    );
  }
  return <Skeleton className="h-full w-full" />;
}

export function WidgetMessage({
  icon: Icon = PlugZap,
  title,
  detail,
  tone = 'muted',
  action,
}: {
  icon?: LucideIcon;
  title: string;
  detail?: string | null;
  tone?: 'muted' | 'warning';
  action?: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1.5 px-3 text-center">
      <Icon
        className={cn('h-5 w-5', tone === 'warning' ? 'text-fresh-salmon-dark' : 'text-muted-foreground/60')}
        strokeWidth={1.8}
      />
      <p className="text-sm font-medium text-foreground">{title}</p>
      {detail && <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">{detail}</p>}
      {action}
    </div>
  );
}

export function WidgetError({ message }: { message: string }) {
  return <WidgetMessage icon={AlertTriangle} tone="warning" title="Couldn't load this data" detail={message} />;
}

/** Signed % change chip, coloured by whether the move is good for the metric. */
export function DeltaBadge({
  delta,
  direction,
  label,
  className,
}: {
  delta: number | null;
  direction: MetricDirection;
  label?: string;
  className?: string;
}) {
  if (delta == null || !Number.isFinite(delta)) {
    return <span className={cn('text-xs text-muted-foreground', className)}>{label ? `— ${label}` : '—'}</span>;
  }
  const up = delta > 0.0005;
  const down = delta < -0.0005;
  const good = direction === 'neutral' ? null : direction === 'up' ? up : down;
  const bad = direction === 'neutral' ? null : direction === 'up' ? down : up;
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const pct = `${delta > 0 ? '+' : ''}${(delta * 100).toFixed(Math.abs(delta) < 0.1 ? 1 : 0)}%`;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium tabular-nums',
        good ? 'text-bright-moss-dark' : bad ? 'text-fresh-salmon-dark' : 'text-muted-foreground',
        className,
      )}
    >
      <Icon className="h-3 w-3" strokeWidth={2.2} />
      {pct}
      {label && <span className="font-normal text-muted-foreground">{label}</span>}
    </span>
  );
}

export const TOOLTIP_STYLE = {
  background: '#fff',
  border: `1px solid ${BRAND.linenDark}`,
  borderRadius: 8,
  fontSize: 12,
  padding: '6px 10px',
} as const;

interface TooltipEntry {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string | Array<number | string>;
  color?: string;
  stroke?: string;
  fill?: string;
  payload?: Record<string, unknown>;
}

export interface TooltipSeriesInfo {
  label: string;
  unit: Unit;
  color?: string;
  /** Array values (bands) are shown as "lo – hi". */
  range?: boolean;
}

/**
 * Shared Recharts tooltip body. Keys map to a label + unit so every chart
 * formats cents/percent/counts consistently without each one wiring a
 * formatter.
 */
export function ChartTooltipContent({
  active,
  payload,
  label,
  grain,
  infoByKey,
  labelFormatter,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
  grain?: TimeGrain;
  infoByKey: Record<string, TooltipSeriesInfo>;
  labelFormatter?: (label: string) => string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const heading = label == null ? '' : labelFormatter ? labelFormatter(String(label)) : grain ? formatBucket(String(label), grain, true) : String(label);
  const seen = new Set<string>();
  return (
    <div style={TOOLTIP_STYLE} className="shadow-sm">
      {heading && <div className="mb-1 font-medium text-foreground">{heading}</div>}
      <div className="space-y-0.5">
        {payload.map((entry, i) => {
          const key = String(entry.dataKey ?? entry.name ?? i);
          if (seen.has(key)) return null;
          seen.add(key);
          const info = infoByKey[key];
          if (!info) return null;
          const value = entry.value;
          if (value == null) return null;
          let text: string;
          if (Array.isArray(value)) {
            const [lo, hi] = value.map((v) => Number(v));
            text = `${formatUnit(lo, info.unit, { compact: true })} – ${formatUnit(hi, info.unit, { compact: true })}`;
          } else {
            text = formatUnit(Number(value), info.unit);
          }
          return (
            <div key={key} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: info.color ?? entry.color ?? entry.stroke ?? entry.fill ?? BRAND.humus }}
                />
                {info.label}
              </span>
              <span className="font-medium tabular-nums text-foreground">{text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LegendRow({ items, className }: { items: Array<{ label: string; color: string; dashed?: boolean }>; className?: string }) {
  if (items.length === 0) return null;
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground', className)}>
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className="inline-block h-0.5 w-3 rounded-full"
            style={{
              background: item.dashed ? 'transparent' : item.color,
              borderTop: item.dashed ? `2px dashed ${item.color}` : undefined,
              height: item.dashed ? 0 : undefined,
            }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}
